/**
 * In-memory access-token store.
 *
 * The access token deliberately lives in a module-scope variable, NOT in
 * localStorage/sessionStorage, so it cannot be exfiltrated by XSS reading
 * storage. Session persistence across page reloads comes from the httpOnly
 * refresh-token cookie: on boot (or on any 401) we call /auth/refresh and
 * get a fresh access token.
 */

let accessToken: string | null = null;
let refreshPromise: Promise<boolean> | null = null;

async function doRefresh(): Promise<boolean> {
  try {
    const res = await fetch(
      `${import.meta.env.VITE_API_BASE_URL ?? "/api"}/auth/refresh`,
      { method: "POST", credentials: "include" },
    );
    if (!res.ok) {
      accessToken = null;
      return false;
    }
    const json = (await res.json()) as { data: { accessToken: string } };
    accessToken = json.data.accessToken;
    return true;
  } catch {
    accessToken = null;
    return false;
  }
}

export const tokenStore = {
  get: () => accessToken,
  set: (token: string | null) => {
    accessToken = token;
  },
  /** Deduplicated refresh — concurrent 401s share one refresh request. */
  refresh: (): Promise<boolean> => {
    refreshPromise ??= doRefresh().finally(() => {
      refreshPromise = null;
    });
    return refreshPromise;
  },
};
